// Gestionnaire de persistance pour les données Company
import { Company } from '../types/company';

const COMPANY_STORAGE_KEY = 'wanzo_company_profile';
const COMPANY_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedCompany {
  data: Company;
  timestamp: number;
  ownerId: string; // ID du propriétaire/admin
}

export class CompanyPersistence {
  
  /**
   * Sauvegarder le profil d'entreprise en cache
   */
  static save(company: Company, ownerId: string): void {
    // ✨ ROBUSTESSE: Vérifier que company est valide avant de sauvegarder
    if (!company || !company.id) {
      console.warn('⚠️ Tentative de sauvegarde d\'une entreprise invalide:', company);
      return;
    }
    
    try {
      const cachedData: CachedCompany = {
        data: company,
        timestamp: Date.now(),
        ownerId
      };
      localStorage.setItem(COMPANY_STORAGE_KEY, JSON.stringify(cachedData));
      console.log('💾 Profil d\'entreprise sauvegardé en cache:', company.id);
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde du profil d\'entreprise:', error);
    }
  }

  /**
   * Récupérer le profil d'entreprise depuis le cache
   */
  static load(ownerId: string): Company | null {
    try {
      const stored = localStorage.getItem(COMPANY_STORAGE_KEY);
      if (!stored) return null;

      const cachedData: CachedCompany = JSON.parse(stored);
      
      // Vérifier que le cache appartient au bon utilisateur
      if (cachedData.ownerId !== ownerId) {
        console.log('🔄 Cache d\'entreprise pour un autre utilisateur, suppression');
        this.clear();
        return null;
      }

      // Vérifier si le cache n'est pas expiré
      const isExpired = Date.now() - cachedData.timestamp > COMPANY_CACHE_DURATION;
      if (isExpired) {
        console.log('⏰ Cache d\'entreprise expiré, suppression');
        this.clear();
        return null;
      }

      console.log('📦 Profil d\'entreprise récupéré depuis le cache:', cachedData.data.id);
      return cachedData.data;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération du profil d\'entreprise:', error);
      this.clear();
      return null;
    }
  }

  /**
   * Vérifier si un cache valide existe
   */
  static hasValidCache(ownerId: string): boolean {
    const company = this.load(ownerId);
    return company !== null;
  }

  /**
   * Supprimer le cache
   */
  static clear(): void {
    localStorage.removeItem(COMPANY_STORAGE_KEY);
    console.log('🗑️ Cache d\'entreprise supprimé');
  }

  /**
   * Mettre à jour seulement certains champs du cache
   */
  static update(updates: Partial<Company>, ownerId: string): Company | null {
    const existing = this.load(ownerId);
    if (!existing) return null;

    const updated = { ...existing, ...updates };
    this.save(updated, ownerId);
    return updated;
  }

  /**
   * Obtenir les informations du cache sans les données
   */
  static getCacheInfo(ownerId: string): { exists: boolean; isValid: boolean; age: number } {
    try {
      const stored = localStorage.getItem(COMPANY_STORAGE_KEY);
      if (!stored) return { exists: false, isValid: false, age: 0 };

      const cachedData: CachedCompany = JSON.parse(stored);
      const age = Date.now() - cachedData.timestamp;
      const isValid = cachedData.ownerId === ownerId && age <= COMPANY_CACHE_DURATION;

      return {
        exists: true,
        isValid,
        age
      };
    } catch {
      return { exists: false, isValid: false, age: 0 };
    }
  }
}

// Fonction utilitaire pour debug
export function debugCompanyCache(ownerId: string) {
  const info = CompanyPersistence.getCacheInfo(ownerId);
  const company = CompanyPersistence.load(ownerId);
  
  console.log('🔍 Debug cache entreprise:');
  console.log('- Existe:', info.exists);
  console.log('- Valide:', info.isValid);
  console.log('- Âge:', Math.round(info.age / 1000), 'secondes');
  console.log('- Données:', company ? `${company.name} (${company.id})` : 'Aucune');
  
  return { info, company };
}

// Exposer en mode dev
if (import.meta.env.DEV) {
  Object.assign(window, { CompanyPersistence, debugCompanyCache });
}
